import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import React, { useCallback, useContext, useMemo } from 'react'
import BedbankMultiSameRoomRoomOption from './BedbankMultiSameRoomRoomOption'
import { useBedbankSingleRoomBookNowCallback, useBedbankSingleRoomAddToUserCartCallback } from 'hooks/OfferPage/useBedbankBuyCallbacks'
import { OfferPageActions, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import { countWhere } from 'lib/array/arrayUtils'
import { occupantsFitAnyRoomCapacity } from 'lib/offer/occupancyUtils'
import { smartScrollIntoViewIfNeeded } from 'lib/window/scrollUtils'
import { ROOM_MODE_TOGGLE_SECTION_ID } from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import ModalContext from 'contexts/ModalContext'
import AccommodationAmenitiesAndPoliciesModal from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesModal'
import { RoomTypeAvailabilityStatus } from 'hooks/useBedbankPackageViews'
import useOfferPageBedbankRates from 'components/OfferPage/hooks/useOfferPageBedbankRates'

interface Props {
  offer: App.BedbankOffer
  packageView: App.BedbankOfferPackageView
  optionVariant: 'stacked' | 'carousel'
  checkInDate?: string
  checkOutDate?: string
  rooms: Array<OfferPageRoom>
  originAirport?: App.Airport
  isLoggedIn: boolean
  isSpoofed?: boolean
  subscriber?: App.OrderSubscriber
  tripPlannerTripId?: string
  fetchingRates?: boolean
  roomListId: string
  roomPosition: number
  connectRooms: boolean
  youWereInterested?: boolean
  availabilityStatus: RoomTypeAvailabilityStatus
}

function BedbankMultiSameRoomRoom({
  offer,
  packageView,
  optionVariant,
  checkInDate,
  checkOutDate,
  rooms,
  originAirport,
  isLoggedIn,
  isSpoofed,
  subscriber,
  fetchingRates,
  roomListId,
  roomPosition,
  connectRooms,
  youWereInterested,
  availabilityStatus,
}: Props) {
  const { rates, isFlexCfmrBedbankPackagesEnabled } = useOfferPageBedbankRates(packageView.effectiveRates, checkInDate, checkOutDate)

  const hasPromotions = !!packageView.promotions.length
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])
  const roomCount = rooms.length
  const validCheckinCheckout = !!(checkInDate && checkOutDate)
  const canSelectRoom = packageView.available && packageView.capacityValid && packageView.show

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: originAirport?.code,
  })

  const canBuyNow = packageView.available && packageView.capacityValid && packageView.show && !fetchingRates

  // use full rates as dep so when user expand/hide rates the list id stay the same
  const rateListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rates, canBuyNow])

  const onBook = useBedbankSingleRoomBookNowCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms,
  })

  const onAddToUserCart = useBedbankSingleRoomAddToUserCartCallback({
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    rooms,
    originAirport,
    isLoggedIn,
    isSpoofed,
    subscriber,
    rateListId,
    roomPosition,
    roomListId,
    flightPrice,
    connectRooms,
  })

  const offerPageDispatch = useOfferPageDispatch()

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, packageView.package.roomId])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, packageView.package.roomId])

  const onSelectIndividualRoomMode = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
    const toggleElement = document.getElementById(ROOM_MODE_TOGGLE_SECTION_ID)
    if (toggleElement) {
      smartScrollIntoViewIfNeeded(toggleElement)
    }
  }, [offerPageDispatch])

  const showModal = useContext(ModalContext)

  const onShowRoomDetails = useCallback(() => {
    if (packageView) {
      showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={packageView.package} />)
    }
  }, [packageView, showModal])

  const carouselWidth = rates.length > 1 ? rem(300) : '100%'

  const validOccupancyRoomsCount = countWhere(
    rooms,
    room => occupantsFitAnyRoomCapacity(room, packageView.package.capacities.combinations, offer),
  )

  const messageBanner = (() => {
    if (validOccupancyRoomsCount === 0) {
      return <MessageBanner kind="informational-primary">
        Room capacity reached. To select it please <TextLink onClick={handleCheckAvailabilityClick}>revise number of guests</TextLink>
      </MessageBanner>
    } else if (validOccupancyRoomsCount > 0 && validOccupancyRoomsCount < rooms.length) {
      return <MessageBanner kind="informational-primary">
        This room type works for some rooms in your group, but not all. To select it, try <TextLink onClick={onSelectIndividualRoomMode}>choosing rooms individually.</TextLink>
      </MessageBanner>
    } else if (!fetchingRates && !validCheckinCheckout && !packageView.effectiveRates.length && validOccupancyRoomsCount === rooms.length) {
      return <TextButton size="medium" kind="primary" fit="full-width" onClick={handleCheckAvailabilityClick}>Check availability</TextButton>
    } else if (!fetchingRates && validCheckinCheckout && !canSelectRoom && (!packageView.available || !packageView.show) && packageView.capacityValid) {
      if (availabilityStatus === 'unavailable') {
        return <MessageBanner
          kind="informational-primary"
          icon={<LineExclamationCircleIcon />}
          heading="This room is sold out on your dates or max number of guests exceeded"
        >
          Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink>
        </MessageBanner>
      } else if (availabilityStatus === 'partiallyAvailable') {
        return <MessageBanner kind="informational-primary">
          This room type works for some rooms in your group, but not all. To select it, try <TextLink onClick={onSelectIndividualRoomMode}>choosing rooms individually.</TextLink>
        </MessageBanner>
      }
    }
  })()

  return <div data-testid="multi-same-room-room">
    {youWereInterested && <MessageBanner
      kind="promotional-primary"
      icon={<LineSightseeingIcon />}
    >
      <b>You were interested in this option</b>
    </MessageBanner>}
    <AccommodationSameRoomTile roomPackage={packageView.package}>
      <AccommodationRoomTileImage
        roomPackage={packageView.package}
      />
      <AccommodationSameRoomTileDetails
        roomPackage={packageView.package}
        roomCount={roomCount}
        urgencyLabels={<>
          {hasPromotions && <BookingsCountLabel
            bookings={packageView.package.stats.booked}
            threshold="room"
          />}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        onShowRoomDetails={onShowRoomDetails}
      >
        <LoadingIndicator visible={fetchingRates} floating opaque />
        {messageBanner}
        {optionVariant === 'carousel' && rates.length > 0 &&
          <CardCarousel width={carouselWidth} gutterSize={20} gap={12} snap="start">
            {rates.map((rate, index) => <BedbankMultiSameRoomRoomOption
              key={rate.id}
              rate={rate}
              packageNumber={index + 1}
              packageView={packageView}
              onBook={onBook}
              onAddToUserCart={onAddToUserCart}
              isFlexCfmrBedbankPackagesEnabled={isFlexCfmrBedbankPackagesEnabled}
            />)}
          </CardCarousel>}
        {optionVariant === 'stacked' && rates.map((rate, index) => <BedbankMultiSameRoomRoomOption
          key={rate.id}
          rate={rate}
          packageNumber={index + 1}
          packageView={packageView}
          onBook={onBook}
          onAddToUserCart={onAddToUserCart}
          isFlexCfmrBedbankPackagesEnabled={isFlexCfmrBedbankPackagesEnabled}
        />)}
      </AccommodationSameRoomTileDetails>
    </AccommodationSameRoomTile>
  </div>
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'checkInDate'
  | 'checkOutDate'
  | 'rooms'
  | 'originAirport'
  | 'isLoggedIn'
  | 'isSpoofed'
  | 'subscriber'
  | 'connectRooms'
>

export default withOfferPageAndReduxState((offerState, appState): MappedProps => ({
  offer: offerState.bedbankOffer!,
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  rooms: offerState.rooms,
  subscriber: getEnquirySubscriber(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isSpoofed: appState.auth.account.isSpoofed,
  originAirport: offerState.airport,
  connectRooms: !!offerState.isMultiRoomConnected,
}), BedbankMultiSameRoomRoom)
